import { useCommissionStatsQuery } from '@/hooks/queries/use-commission-stats-query';
import { Box, Center, Text } from '@chakra-ui/react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CommissionStats } from './CommissionStats';

interface CommissionStatsGridProps {}

const CommissionStatsGrid: React.FC<CommissionStatsGridProps> = () => {
  const { t } = useTranslation();

  // Read date range from URL query parameters (with "stats" prefix from DateRangeFilter)
  const [statsStartDate] = useQueryState('statsStartDate', parseAsString);
  const [statsEndDate] = useQueryState('statsEndDate', parseAsString);

  // Build filters object for the API query
  const filters = useMemo(
    () => ({
      startDate: statsStartDate || undefined,
      endDate: statsEndDate || undefined,
    }),
    [statsStartDate, statsEndDate]
  );

  const { data: stats, isLoading, error } = useCommissionStatsQuery(filters);

  // Handle error state
  if (error) {
    console.error('Failed to fetch commission statistics:', error);
    return (
      <Center py={8}>
        <Box textAlign="center">
          <Text color="red.500" mb={2}>
            {t('commissions.stats.errorLoading')}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {t('commissions.stats.errorRefresh')}
          </Text>
        </Box>
      </Center>
    );
  }

  // Show loading state
  if (isLoading || !stats) {
    // Show loading skeleton
    return (
      <CommissionStats
        stats={{
          totalGenerated: { amount: 0, currencyCode: 'EUR' },
          totalPending: { amount: 0, currencyCode: 'EUR' },
          totalProcessing: { amount: 0, currencyCode: 'EUR' },
          totalPaid: { amount: 0, currencyCode: 'EUR' },
          totalCancelled: { amount: 0, currencyCode: 'EUR' },
          previousPeriodTotal: { amount: 0, currencyCode: 'EUR' },
          growthPercentage: 0,
          period: { startDate: null, endDate: null },
        }}
        isLoading={true}
      />
    );
  }

  return <CommissionStats stats={stats} isLoading={false} />;
};

export default CommissionStatsGrid;
